<template>
  <v-card flat>
    <!--
      -- TOOLBAR DIALOGS
      -->
    <!-- PREVIEW -->
    <v-dialog v-model="ui.sendPreviewDialog" persistent max-width="500">
      <send-email-preview-dialog
        :dealer-id="dealer.id"
        :rooftop-id="campaign.dealer_rooftop_id"
        :email-footer-id="campaign.email_template_footer_id"
        v-on:close="handleEmailPreviewClose"
        v-on:send="handleEmailPreviewSend"
        v-on:error="handleEmailPreviewError"
      >
      </send-email-preview-dialog>
    </v-dialog>

    <!-- HISTORY -->
    <v-dialog
      v-model="showChangeHistory"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <change-history
        v-model="changeHistory"
        :title="campaign.name"
        :showClose="true"
        v-on:close="showChangeHistory = false"
      >
      </change-history>
    </v-dialog>

    <v-dialog
      v-model="showCopyCampaignDialog"
      persistent
      max-width="500"
    >
      <copy-campaign-dialog
        :initial-dealer-id="dealer.id"
        v-on:on-copy="copyCampaign"
        v-on:close="showCopyCampaignDialog = false"
      >
      </copy-campaign-dialog>
    </v-dialog>
    <!-- END HISTORY -->
    <!-- END TOOLBAR DIALOGS -->

    <!-- CAMPAIGN ACTIONS TOOLBAR -->
    <v-toolbar color="secondary" dense>
      <!--
        -- THESE ACTION BUTTONS ACTIONS WERE TOO DIFFERENT TO ABSTRACT THE RENDERING
        -- NOTE: BEHAVIOR IS DUPLICATED between LARGE and SMALL VIEWPORTS
      -->
      <!--
        -- LARGE VIEWPORT
        -->
      <v-toolbar-items class="hidden-sm-and-down">
        <!-- PREVIEW -->
        <v-btn
          :depressed="true"
          color="secondary"
          :loading="ui.sendingEmail"
          @click.stop="ui.sendPreviewDialog = true"
        >
          <v-icon left dark>mdi-magnify</v-icon>
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon dark>mdi-cached</v-icon>
            </span>
          </template>
          Send Preview
        </v-btn>

        <!-- HISTORY -->
        <v-btn
          v-if="!isNewCampaign"
          :depressed="true"
          color="secondary"
          @click.stop="showChangeHistory = true"
        >
          <v-icon left dark>mdi-history</v-icon>
          Change History
        </v-btn>

        <!-- SPLIT TESTING -->
        <v-btn
          :depressed="true"
          color="secondary"
          v-if="canSplitTest"
          @click.stop="confirmSplitTestStart"
        >
          <v-icon left dark>mdi-ab-testing</v-icon>
          Split Test Campaign
        </v-btn>

        <!-- SAVES -->
        <v-btn
          :depressed="true"
          color="secondary"
          type="submit"
          @click.stop.prevent="saveNoSchedule"
          v-if="!splitTestRunning"
        >
          <v-icon left dark>mdi-content-save</v-icon>
          Save Unscheduled
        </v-btn>

        <!-- SAVE AND SCHEDULE -->
        <v-btn
          :depressed="true"
          color="secondary"
          v-if="allowScheduling && !splitTestRunning"
          type="submit"
          @click.stop.prevent="validateAndSubmit"
        >
          <v-icon left dark>mdi-clock-outline</v-icon>
          Save and Schedule
        </v-btn>

        <!-- Copy Campaign -->
        <v-btn
          :depressed="true"
          color="secondary"
          v-if="isNewCampaign"
          type="submit"
          @click.stop.prevent="showCopyCampaignDialog = true"
        >
          <v-icon left dark>mdi-content-duplicate</v-icon>
          Copy Campaign
        </v-btn>

        <!-- CLOSE -->
        <v-btn
          :depressed="true"
          color="secondary"
          v-if="showCancel"
          @click="doCancel()"
        >
            <v-icon left dark>mdi-close</v-icon>
            Close
        </v-btn>
      </v-toolbar-items>

      <!--
        -- SMALL VIEWPORT
        -->
      <v-spacer/>
      <v-menu right bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            dark
            icon
            v-bind="attrs"
            v-on="on"
            class="hidden-md-and-up"
          >
            <v-icon dark>mdi-menu</v-icon>
          </v-btn>
        </template>
        <v-list>
            <!-- PREVIEW -->
            <v-list-item @click.stop="ui.sendPreviewDialog = true">
              <v-list-item-title><v-icon left>mdi-magnify</v-icon>Send Preview</v-list-item-title>
            </v-list-item>

            <!-- HISTORY -->
            <v-list-item v-if="!isNewCampaign" @click.stop="showChangeHistory = true">
              <v-list-item-title><v-icon left>mdi-history</v-icon>Change History</v-list-item-title>
            </v-list-item>

            <!-- SPLIT TEST -->
            <v-list-item v-if="canSplitTest" @click.stop="confirmSplitTestStart">
              <v-list-item-title><v-icon left>mdi-ab-testing</v-icon>Split Test Campaign</v-list-item-title>
            </v-list-item>

           <!-- SAVE -->
           <v-list-item v-if="!splitTestRunning" @click.stop.prevent="saveNoSchedule">
              <v-list-item-title><v-icon left>mdi-content-save</v-icon>Save Unscheduled</v-list-item-title>
            </v-list-item>

            <!-- SAVE AND SCHEDULE -->
            <v-list-item v-if="allowScheduling && !splitTestRunning" @click.stop.prevent="validateAndSubmit">
              <v-list-item-title><v-icon left>mdi-clock-outline</v-icon>Save and Schedule</v-list-item-title>
            </v-list-item>

            <!-- CLOSE -->
            <v-list-item v-if="showCancel" @click="doCancel()">
              <v-list-item-title><v-icon left>mdi-close</v-icon>Cancel</v-list-item-title>
            </v-list-item>
        </v-list>
      </v-menu>

    </v-toolbar>

    <v-card-text>
      <!-- A/B TESTING -->
      <v-expand-transition>
        <v-card
          v-if="isSplitTested || showSplitTestSetup"
        >
          <template v-if="isSplitTested">
            <split-test-edit
              ref="splitTestEdit"
              v-model="activeSplitTest"
              :email-campaign="campaign"
              v-on:split-test-deleted="loadCampaign"
              v-on:split-test-complete="loadCampaign"
            >
            </split-test-edit>
          </template>
          <template v-else>
            <split-test-setup
              :v-show="canSplitTest"
              :email-campaign-id="this.emailCampaignId"
              v-on:split-test-created="addSplitTestToCampaign"
              v-on:cancel="showSplitTestSetup = false"
            >
            </split-test-setup>
          </template>
        </v-card>
      </v-expand-transition>
      <v-form ref="emailCampaignForm">
        <!-- CAMPAIGN DATA ENTRY -->
        <v-card flat>
          <!-- MAIN -->
          <v-row>
            <!-- LHS -->
            <v-col md="6">
              <v-card>
                <v-toolbar dense flat dark color="primary">
                  <v-toolbar-title>
                    <span class>{{ titleDesc }}</span>
                  </v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <v-row no-gutters>
                    <v-col cols="4">
                      <v-select
                        v-model="campaign.email_type"
                        :items="emailTypes"
                        v-validate="'required'"
                        data-vv-name="email_type"
                        :error-messages="errors.collect('email_type')"
                        label="Email Type"
                        required
                        :persistent-hint="true"
                        hint="Is this email following up (transactional) or selling/promoting a product/event? Accurate classification is important for deliverability."
                        chips
                        small-chips
                      />
                    </v-col>
                    <v-col cols="7" offset="1">
                      <dynamic-combobox
                        v-model="campaign.tags"
                        :initial-items="campaignTags"
                        :persistent-hint="true"
                        label="Tags"
                        :small-chips="false"
                        :solo="false"
                        hint="Tag to allow grouping by type"
                        class="mt-3"
                      />
                    </v-col>
                  </v-row>

                  <rooftop-select
                    v-model="campaign.dealer_rooftop_id"
                    :dealer-id="campaign.dealer_id"
                  />

                  <v-text-field
                    v-model="campaign.name"
                    v-validate="'required'"
                    data-vv-name="name"
                    :error-messages="errors.collect('name')"
                    label="Name"
                    required
                    class="mt-6"
                  />

                  <v-textarea v-model="campaign.notes" label="Notes" ref="notes"/>

                  <v-text-field
                    v-model="campaign.email_subject"
                    v-validate="'required'"
                    data-vv-name="campaign.email_subject"
                    :error-messages="errors.collect('campaign.email_subject')"
                    label="Email Subject"
                    required
                  />

                  <v-text-field
                    v-model="campaign.preheader"
                    label="Email PreHeader"
                  />

                  <email-sender-select
                    v-model="campaign.email_sender_id"
                    :dealer_id="campaign.dealer_id"
                    :rooftop_id="campaign.dealer_rooftop_id"
                    v-validate="'required'"
                    data-vv-name="email_sender_id"
                    :error-messages="errors.collect('email_sender_id')"
                    required
                  />

                  <dealer-select
                    v-show="!dealerScopedView"
                    v-model="campaign.dealer_id"
                    :dealer_id="campaign.dealer_id"
                    v-validate="'required'"
                    data-vv-name="dealer_id"
                    data-vv-as="Dealer"
                    :error-messages="errors.collect('dealer_id')"
                    required
                  />
                  <email-template-select
                    v-model="campaign.email_template_id"
                    :dealer_id="campaign.dealer_id"
                    :email_template_id="campaign.email_template_id"
                    :email-template-footer-id="campaign.email_template_footer_id"
                    v-on:selected-email-template="onEmailTemplateChange"
                    v-validate="'required'"
                    data-vv-name="email_template_id"
                    data-vv-as="Email Template"
                    :error-messages="errors.collect('email_template_id')"
                    :show-preview="true"
                    required
                  />

                  <html-snippet-select
                    v-model="campaign.email_template_footer_id"
                    v-validate="'required'"
                    data-vv-name="email_template_footer_id"
                    data-vv-as="Email Template Footer"
                    :error-messages="errors.collect('email_template_id')"
                    :show-preview="true"
                    label="Email Template Footer"
                    snippet-type-filter="EmailTemplateFooter"
                    required
                    v-on:default-snippet-id="setDefaultSnippet"
                  >
                  </html-snippet-select>

                  <datasource-select
                    v-model="campaign.datasource_id"
                    :dealer_id="campaign.dealer_id"
                    :datasource_id="campaign.datasource_id"
                    v-on:selected-datasource="onDatasourceChange"
                    v-validate="'required'"
                    data-vv-name="datasource_id"
                    data-vv-as="Datasource"
                    :error-messages="errors.collect('datasource_id')"
                    required
                  />

                <mapping-dialog
                  v-if="campaign.datasource && campaign.email_template && campaign.field_mapping"
                  ref="campaignMapping"
                  v-model="campaign.field_mapping"
                  :datasource="campaign.datasource"
                  :email-template="campaign.email_template"
                  lhs-title="Email Template Field"
                  rhs-title="DataSource Field"
                  default-value-title="Fallback Value"
                />

                <v-text-field
                  v-model="marketingPhone"
                  v-mask="'###-###-####'"
                  data-vv-name="name"
                  :error-messages="errors.collect('marketing_phone')"
                  label="Custom Marketing Phone"
                  class="mt-6"
                  type="tel"
                  persistent-hint
                  hint="Phone must be valid. (XXX-XXX-XXXX) format"
                />

                <v-text-field
                  v-model="campaign.offer_disclaimer"
                  label="Offer Disclaimer"
                  class="mt-6"
                >
                </v-text-field>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- RHS -->
            <v-col md="6">
              <!-- SCHEDULE -->
              <v-card>
                <v-toolbar dense flat dark color="primary">
                  <v-toolbar-title>
                    <span class>Schedule</span>
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-radio-group v-model="campaign.schedule_type" row class="mt-6">
                    <v-radio label="Single" value="single"></v-radio>
                    <v-radio label="Recurring" value="recurring"></v-radio>
                  </v-radio-group>
                </v-toolbar>

                <v-card-text>
                  <ampm-time-picker
                    v-model="campaign.execution_time"
                    v-validate="'required'"
                    data-vv-name="execution_time"
                    :error-messages="errors.collect('execution_time')"
                    label="Execute At"
                    :allowed-interval-minutes="ui.timePickerInterval"
                    @change="setCampaignProperty('execution_time', $event)"
                  />

                  <ymd-datepicker
                    v-show="campaign.schedule_type === 'single'"
                    v-model="campaign.execution_date"
                    :initialDate="campaign.execution_date"
                    v-validate="{ required: this.campaign.schedule_type === 'single' }"
                    data-vv-name="execution_date"
                    :error-messages="errors.collect('execution_date')"
                    label="Execute On"
                    required
                    :date.sync="campaign.execution_date"
                  />

                  <ymd-datepicker
                    v-show="campaign.schedule_type === 'recurring'"
                    v-model="campaign.schedule_start_on"
                    :initialDate="campaign.schedule_start_on || defaultScheduleStartOn()"
                    v-validate="{ required: this.campaign.schedule_type === 'recurring' }"
                    data-vv-name="schedule_start_on"
                    :error-messages="errors.collect('schedule_start_on')"
                    label="Start On"
                    required
                    :date.sync="campaign.schedule_start_on"
                  />

                  <v-row align="center" justify="center">
                    <v-col cols="9" v-show="campaign.schedule_type === 'recurring'" class="ma-0 mp-0">
                      <v-text-field
                        v-model="campaign.schedule"
                        v-validate="{ required: this.campaign.schedule_type === 'recurring' }"
                        data-vv-name="schedule"
                        :error-messages="errors.collect('schedule')"
                        label="Schedule"
                        prepend-icon="mdi-calendar"
                        :hint="scheduleDescription"
                        :persistent-hint="true"
                        class="pr-6"
                      />
                    </v-col>
                    <v-col cols="3" v-show="campaign.schedule_type === 'recurring'" class="ma-0 mp-0">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            href="https://rrule.dealerapps.io/"
                            target="_blank"
                          >Create a Rule</v-btn>
                        </template>
                        <span>
                          Once you create the schedule on the resulting link,
                          <br />Copy and Paste the RRULE schedule in the schedule field
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- RULES -->
              <v-card class="mt-4">
                <v-toolbar dense flat dark color="primary">
                  <v-toolbar-title>
                    <span class>Rules</span>
                  </v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <interval-input
                    v-model="campaign.ruleExcludeByCampaignForInterval"
                    label="Exclude By Campaign"
                    description="Exclude if recipient has received emails from this campaign in past"
                  >
                  </interval-input>
                  <v-divider></v-divider>
                  <interval-input
                    v-model="campaign.ruleExcludeBySenderForInterval"
                    label="Exclude By Sender"
                    description="Exclude if recipient has received emails from any campaign in past"
                  >
                  </interval-input>
                  <v-divider></v-divider>
                  <v-row no-gutters>
                    <v-col cols="3" class="mt-4">
                      <v-label>Limit to one email per VIN per campaign</v-label>
                      <p class="red--text" v-if="!campaign.rule_limit_once_per_email_vin_per_campaign">Rule turned off</p>
                    </v-col>
                    <v-col cols="4">
                      <v-checkbox v-model="campaign.rule_limit_once_per_email_vin_per_campaign"></v-checkbox>
                    </v-col>
                     <v-col cols="4" class="mt-2 ml-4">
                      <p>Limit to one email per VIN per campaign. If VIN is not available, the rule is one email per campaign. Use this to prevent duplicate emails for the same email address regarding the same VIN. Should apply to all Lifecycle emails.</p>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Google Analytics -->
              <v-card class="mt-4">
                <v-toolbar dense flat dark color="primary">
                  <v-toolbar-title>
                    <span class>Google Analytics</span>
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-switch v-model="campaign.google_analytics_enabled" :label="gaLabel" class="mt-4"></v-switch>
                </v-toolbar>
                <v-card-text v-show="campaign.google_analytics_enabled">
                  <v-row no-gutters>
                    <v-col cols="5">
                      <v-text-field
                        v-model="campaign.google_analytics_config.utm_campaign"
                        v-validate="utmCampaignValidationRule"
                        data-vv-name="campaign.google_analytics_config.utm_campaign"
                        :error-messages="errors.collect('campaign.google_analytics_config.utm_campaign')"
                        data-vv-as="UTM Campaign"
                        label="UTM Campaign"
                        required
                        persistent-hint
                        hint="The Campaign Name without spaces"
                      >
                        <template v-slot:append-outer>
                             <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn small v-bind="attrs" v-on="on" @click="syncUtmCampaignName">
                                  <v-icon small>mdi-sync</v-icon></v-btn>
                              </template>
                              <span>Sync with Campaign Name</span>
                             </v-tooltip>
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-model="campaign.google_analytics_config.utm_source"
                        v-validate="'alpha_dash'"
                        data-vv-name="campaign.google_analytics_config.utm_source"
                        :error-messages="errors.collect('campaign.google_analytics_config.utm_source')"
                        data-vv-as="UTM Source"
                        label="UTM Source"
                        required
                        persistent-hint
                        hint="The referrer: (e.g. google, newsletter, call_center)"
                      />
                    </v-col>
                    <v-col cols="1">
                      <!-- space between cols -->
                    </v-col>
                    <v-col cols="5">
                      <v-text-field
                        v-model="campaign.google_analytics_config.utm_medium"
                        v-validate="'alpha_dash'"
                        data-vv-name="campaign.google_analytics_config.utm_medium"
                        :error-messages="errors.collect('campaign.google_analytics_config.utm_medium')"
                        data-vv-as="UTM Medium"
                        label="UTM Medium"
                        required
                        persistent-hint
                        hint="Marketing medium: (e.g. cpc, banner, email)"
                      />

                      <!-- THIS FIELD is valid but turned off because SplitTesting puts a value in
                        -- HERE - turning off input but keeping for reference in case we turn
                        -- back on
                     <v-text-field
                        v-model="campaign.google_analytics_config.utm_content"
                        data-vv-name="campaign.google_analytics_config.utm_content"
                        v-validate="'alpha_dash'"
                        :error-messages="errors.collect('campaign.google_analytics_config.utm_content')"
                        data-vv-as="UTM Content"
                        label="UTM Content"
                        required
                        persistent-hint
                        hint="Use to differentiate ads"
                      />
                      -->
                      <v-text-field
                        v-model="campaign.google_analytics_config.utm_term"
                        data-vv-name="campaign.google_analytics_config.utm_term"
                        v-validate="'alpha_dash'"
                        :error-messages="errors.collect('campaign.google_analytics_config.utm_term')"
                        data-vv-as="UTM Term"
                        label="UTM Term"
                        required
                        persistent-hint
                        hint="Identify the paid keywords"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-form>
    </v-card-text>
    <!-- Overlay for Saves -->
    <v-overlay :value="isSavingCampaign">
      <div class="text-center">
        <h2 class="mb-6">Saving Email Campaign</h2>
        <v-progress-circular indeterminate size="72"></v-progress-circular>
      </div>
    </v-overlay>
    <!-- Dialog for starting Split Test -->
    <v-dialog
      v-model="showStartSplitTestDialog"
      max-width="600"
    >
        <v-card>
          <v-toolbar
            color="primary"
            dense
            dark
          >
            Start the Split Test
          </v-toolbar>
          <v-card-text>
            <p class="text-h6 pt-8">Start Split Testing</p>
            <p class="text-body1">You will not be able to make changes to this campaign until split testing is stopped.</p>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn @click.stop="showStartSplitTestDialog = false" >Cancel</v-btn>
            <v-btn color="primary" @click="startSplitTest">Start Test</v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  DealerSelect,
  DatasourceSelect,
  EmailTemplateSelect,
  YMDDatePicker,
  AmPmTimePicker,
  EmailSenderSelect,
  Interval,
  SendEmailPreviewDialog,
  DynamicComboBox,
  HtmlSnippetSelect,
  RooftopSelect
} from '@/components/inputs'

import CopyCampaignDialog from './CopyCampaignDialog'
import ChangeHistory from '@/components/shared/ChangeHistory'
import MappingDialog from './MappingDialog'
import SplitTestSetup from './SplitTestSetup'
import SplitTestEdit from './SplitTestEdit'

import { mapState, mapMutations } from 'vuex'

import { EmailPreviewApi, EmailCampaignApi } from '@/services'

const R = require('ramda')
const rrulestr = require('rrule').rrulestr

const STATUS_ACTIVE = 'active'
// const STATUS_COMPLETE = 'complete'

export default {
  name: 'email-campaign',
  components: {
    [DealerSelect.name]: DealerSelect,
    [DatasourceSelect.name]: DatasourceSelect,
    [EmailTemplateSelect.name]: EmailTemplateSelect,
    [EmailSenderSelect.name]: EmailSenderSelect,
    [HtmlSnippetSelect.name]: HtmlSnippetSelect,
    [YMDDatePicker.name]: YMDDatePicker,
    [AmPmTimePicker.name]: AmPmTimePicker,
    [Interval.name]: Interval,
    [SendEmailPreviewDialog.name]: SendEmailPreviewDialog,
    [DynamicComboBox.name]: DynamicComboBox,
    [ChangeHistory.name]: ChangeHistory,
    [SplitTestSetup.name]: SplitTestSetup,
    [SplitTestEdit.name]: SplitTestEdit,
    [MappingDialog.name]: MappingDialog,
    [CopyCampaignDialog.name]: CopyCampaignDialog,
    [RooftopSelect.name]: RooftopSelect
  },
  provide () {
    // have to provide this to child components
    return {
      $validator: this.$validator
    }
  },
  props: {
    emailCampaignId: {
      type: [Number, String],
      required: false,
      default: null
    },
    allowScheduling: {
      type: Boolean,
      required: false,
      default: true
    },
    showCancel: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  data () {
    return {
      changeHistory: [],
      showChangeHistory: false,
      campaign: this.campaignSkeleton(),
      campaignTags: [],
      ui: {
        timePickerInterval: 15,
        editMapping: false,
        sendPreviewDialog: false,
        sendingEmail: false
      },
      emailTypes: [
        { text: 'Promotional', value: 'promotional' },
        { text: 'Transactional', value: 'transactional' },
        { text: 'CRM', value: 'crm' }
      ],
      showSplitTestSetup: false,
      isSavingCampaign: false,
      showStartSplitTestDialog: false,
      splitTestAccepted: false,
      defaultFooterId: null,
      showCopyCampaignDialog: false
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    ...mapState('ApplicationStore', ['dealerScopedView']),
    scheduleDescription: function () {
      if (this.campaign.schedule) {
        try {
          return rrulestr(this.campaign.schedule).toText()
        } catch (error) {
          return 'Invalid Rule Expression'
        }
      } else {
        return ''
      }
    },
    titleDesc: function () {
      if (this.emailCampaignId) {
        return 'Editing [' + this.campaign.name + ']'
      } else {
        return 'New Email Campaign'
      }
    },
    gaLabel: function () {
      return this.campaign.google_analytics_enabled ? 'Enabled' : 'Disabled'
    },
    campaignName: function () {
      return this.campaign.name
    },
    utmCampaignValidationRule: function () {
      return this.campaign.google_analytics_enabled ? 'required|alpha_dash' : ''
    },
    canSplitTest: function () {
      // has to be a saved campaign to split test
      return !!this.campaign.id && !this.isSplitTested
    },
    isNewCampaign: function () {
      return !this.campaign.id
    },
    isSplitTested: function () {
      return R.pathOr(false, ['active_split_test', 'id'], this.campaign)
    },
    splitTestRunning: function () {
      return R.pathOr(false, ['active_split_test', 'started_at'], this.campaign) &&
        R.pathOr(false, ['active_split_test', 'status'], this.campaign) === STATUS_ACTIVE
    },
    activeSplitTest: {
      get: function () {
        return this.campaign.active_split_test
      },
      set: function (val) {
        this.$set(this.campaign, 'active_split_test', val)
      }
    },
    marketingPhone: {
      get: function () {
        return R.prop('marketing_phone', this.campaign)
      },
      set: function (val) {
        this.$set(this.campaign, 'marketing_phone', val)
      }
    },
    defaultRooftopId () {
      return this.dealer.rooftops.find(r => r.default_rooftop).id
    }
  },
  mounted: function () {
    this.loadCampaign()
  },
  watch: {
    dealer: {
      immediate: true,
      handler (newDealer, oldDealer) {
        if (newDealer.id) {
          this.$set(this.campaign, 'dealer_id', newDealer.id)
          this.$set(this.campaign, 'dealer', newDealer)
        }
      }
    },
    emailCampaignId (newVal, oldVal) {
      this.loadCampaign()
    },
    showChangeHistory (newVal, oldVal) {
      if (newVal === true) {
        this.loadChangeHistory()
      }
    }
  },
  methods: {
    ...mapMutations('DealerStore', ['saveDealer']),
    campaignSkeleton () {
      return {
        name: null,
        notes: null,
        schedule_type: 'single',
        execution_date: null,
        execution_time: null,
        schedule: null,
        dealer_id: this.dealerScopedView ? this.dealer.id : null,
        dealer: this.dealerScopedView ? this.dealer : {},
        dealer_rooftop_id: this.defaultRooftopId,
        email_template_id: null,
        email_template_footer_id: null,
        email_sender_id: null,
        datasource_id: null,
        email_subject: null,
        field_mapping: [],
        ruleExcludeBySenderForInterval: null,
        ruleExcludeByCampaignForInterval: '7 days',
        rule_limit_once_per_email_vin_per_campaign: false,
        google_analytics_enabled: true,
        google_analytics_config: {
          utm_campaign: null,
          utm_source: 'luther_call_center',
          utm_medium: 'email',
          utm_content: null,
          utm_term: null
        },
        email_type: 'promotional',
        tags: []
      }
    },
    runFormValidation () {
      if (this.$refs.campaignMapping) {
        if (!this.$refs.campaignMapping.isMappingValid()) {
          this.$refs.campaignMapping.openMapper()
          return Promise.resolve(false)
        }
      }

      const abValidateMapping = new Promise((resolve, reject) => {
        if (this.isSplitTested) {
          const result = this.$refs.splitTestEdit.validateMapping()
          resolve(result)
        } else {
          // no split test edit screen - not split tested - good to go
          resolve(true)
        }
      })

      const abValidator = this.isSplitTested
        ? this.$refs.splitTestEdit.$validator.validateAll()
        : Promise.resolve(true)

      const campaignValidator = this.$validator.validateAll()

      return Promise.all([abValidator, campaignValidator, abValidateMapping])
        .then(results => {
          // some returns true to a match - if *some*, then not valid
          return !results.some(isValid => isValid === false) // anything not valid invalidates all not valid
        })
    },
    validateAndSubmit () {
      this.runFormValidation().then(isValid => {
        if (isValid) {
          if (this.isSplitTested) {
            if (this.splitTestAccepted) {
              this.saveEmailCampaign(true)
            } else {
              this.showStartSplitTestDialog = true
            }
          } else {
            this.saveEmailCampaign(true)
          }
        }
      })
    },
    saveNoSchedule () {
      this.saveEmailCampaign(false)
    },
    async saveEmailCampaign (shouldSchedule) {
      this.isSavingCampaign = true
      if (this.campaign.schedule_type === 'single') {
        this.$set(this.campaign, 'schedule', null)
      } else {
        this.$set(this.campaign, 'execution_date', null)
      }

      if (shouldSchedule) {
        this.$set(this.campaign, 'status', 'scheduled')
      } else {
        this.$set(this.campaign, 'status', 'unscheduled')
      }

      if (this.isSplitTested) {
        try {
          await EmailCampaignApi.updateSplitTest(this.campaign.id, this.activeSplitTest.id, this.activeSplitTest)
          this.$logger.trace('SplitTestUpdated')
        } catch (error) {
          this.$logger.error('Error saving split test', error.message)
          this.$flash({
            showClose: true,
            message: `Error Saving Email Campaign. ${error.message}`,
            type: 'error',
            duration: 0
          })
          this.isSavingCampaign = false
          return
        }
      }

      try {
        const saveResp = await this.$store.dispatch('EmailCampaignStore/saveEmailCampaign', this.campaign)
        this.$emit('save', saveResp)
        return saveResp
      } catch (error) {
        this.$logger.error(error.message)
        this.$flash({
          showClose: true,
          message: `Error Saving Email Campaign. ${error.message}`,
          type: 'error',
          duration: 0
        })
      } finally {
        this.isSavingCampaign = false
      }
    },
    ymdDate (date) {
      if (!date) return null
      const [month, day, year] = date.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    onDatasourceChange (datasource) {
      // update the id
      this.$set(this.campaign, 'datasource_id', R.prop('id', datasource))
      // update the backing object...
      this.$set(this.campaign, 'datasource', datasource)
    },
    onEmailTemplateChange (emailTemplate) {
      // update the id
      this.$set(this.campaign, 'email_template_id', R.prop('id', emailTemplate))
      // update the backing object...
      this.$set(this.campaign, 'email_template', emailTemplate)
    },
    setCampaignProperty (prop, val) {
      this.$set(this.campaign, prop, val)
    },
    loadCampaign () {
      const emailCampaignPromise = emailCampaignId => {
        if (emailCampaignId) {
          return this.$store
            .dispatch('EmailCampaignStore/getEmailCampaign', {
              id: emailCampaignId
            })
            .then(emailCampaign => {
              return Promise.resolve(emailCampaign)
            })
        } else {
          return Promise.resolve(this.campaignSkeleton())
        }
      }

      const mergeTagPromises = [
        emailCampaignPromise(this.emailCampaignId),
        EmailCampaignApi.allCampaignTags()
      ]
      Promise.all(mergeTagPromises)
        .then(results => {
          // when loading an email campaign, check the dealer - if not matching existing
          // dealer that is set via DealerStore, change it!
          // this is the reload functionality!
          if (results[0].dealer_id !== this.dealer.id) {
            this.saveDealer(results[0].dealer)
          }
          // set the campaign
          this.campaign = results[0]

          // Set the campaign tags
          this.campaignTags = results[1].data

          // check to see if the default footer has arrived yet...
          if (this.defaultFooterId) {
            if (!this.campaign.email_template_footer_id) {
              this.campaign.email_template_footer_id = this.defaultFooterId
            }
          }
        })
        .catch(error => {
          this.$logger.error('Error loading user mappable merge tags or Email Campaign', error.message)
          this.$flash({
            showClose: true,
            message: 'Error loading campaign and associated resources. Reload to try again',
            type: 'error',
            duration: 0
          })
        })
    },
    handleEmailPreviewClose (closeInput) {
      this.ui.sendPreviewDialog = false
    },
    handleEmailPreviewError (errorMessage) {
      this.$flash({
        showClose: true,
        message: 'Email Preview Error' + errorMessage,
        type: 'error'
      })
    },
    handleEmailPreviewSend (emailPreviewSendArgs) {
      const emailAddresses = emailPreviewSendArgs.recipients
      const campaignOverrides = emailPreviewSendArgs.dealer_rooftop_id ? { dealer_rooftop_id: emailPreviewSendArgs.dealer_rooftop_id } : {}

      this.runFormValidation().then(isValid => {
        if (isValid) {
          this.ui.sendingEmail = true
          this.ui.sendPreviewDialog = false
          EmailPreviewApi.sendEmailCampaignPreviewEmail(emailAddresses, { ...this.campaign, ...campaignOverrides })
            .then(() => {
              this.ui.sendingEmail = false
            })
            .catch(error => {
              this.ui.sendingEmail = false
              this.$flash({
                showClose: true,
                message: 'Error sending preview email. ' + error.message,
                type: 'error',
                duration: 0
              })
            })
        } else {
          this.$flash({
            showClose: true,
            message: 'You must have a complete, valid email campaign for a preview',
            type: 'warning',
            duration: 8000
          })
        }
      })
    },
    doCancel () {
      this.$emit('cancel')
    },
    defaultScheduleStartOn () {
      return new Date().toISOString().slice(0, 10)
    },
    syncUtmCampaignName () {
      const baseRegex = /[^\w+-_]+/g

      const utmName = this.campaignName.toString().trim()
        .replaceAll(' ', '_')
        .replaceAll(baseRegex, '')
        .replaceAll('[', '')
        .replaceAll(']', '')
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll(',', '')
        .replaceAll('_-_', '-')
        .replaceAll('__', '_')
        .replaceAll('_-', '_')
        .replaceAll('-_', '_')
        .replaceAll('.', '-')
        .replaceAll('/', '-')
        .replaceAll('\\', '-')

      this.$set(this.campaign.google_analytics_config, 'utm_campaign', utmName)
    },
    loadChangeHistory () {
      EmailCampaignApi.changeHistory(this.campaign.id)
        .then((resp) => {
          this.changeHistory = resp.data
        })
        .catch(error => {
          this.$flash({
            showClose: true,
            message: 'Error loading revision history' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    addSplitTestToCampaign (splitTestData) {
      this.showSplitTestSetup = false
      this.$set(this.campaign, 'active_split_test', splitTestData)
      this.$logger.trace('Updated split test model')
    },
    confirmSplitTestStart () {
      this.showSplitTestSetup = true
    },
    startSplitTest () {
      this.showSplitTestSetup = false
      this.splitTestAccepted = true
      this.validateAndSubmit()
    },
    setDefaultSnippet (snippetId) {
      this.defaultFooterId = snippetId
      // there was a race between loadCampaign & this - do both
      this.$nextTick(() => {
        if (!R.prop('email_template_footer_id', this.campaign)) {
          this.$set(this.campaign, 'email_template_footer_id', snippetId)
        }
      })
    },
    copyCampaign (campaignAttrs) {
      this.campaign = R.mergeDeepRight(this.campaign, campaignAttrs)
      this.syncUtmCampaignName() // re-sync the GA name
      this.showCopyCampaignDialog = false
    }
  }
}
</script>
